import type { Invitation, Pass, Site, Team } from '@sine/sine-core-api-types';

import type { ReceiveSocketEventBasePayload } from '../../../components/sockets';

/* Send Events */

export enum SendSocketEvent {}

// eslint-disable-next-line @typescript-eslint/ban-types
export type SendSocketEventPayloadMap = {};

/* Receive Events */

export enum ReceiveSocketEvent {
  API_KEY_CHANGED = 'API_KEY_CHANGED',
  EXPORT_ASSETS_ERROR = 'EXPORT_ASSETS_ERROR',
  EXPORT_ASSETS_FINISHED = 'EXPORT_ASSETS_FINISHED',
  EXPORT_ASSET_HISTORY_ERROR = 'EXPORT_ASSET_HISTORY_CSV_ERROR',
  EXPORT_ASSET_HISTORY_FINISHED = 'EXPORT_ASSET_HISTORY_CSV_FINISHED',
  EXPORT_ASSET_HISTORY_STARTED = 'EXPORT_ASSET_HISTORY_CSV_STARTED',
  EXPORT_ASSETS_STARTED = 'EXPORT_ASSETS_STARTED',
  EXPORT_INVITATIONS_ERROR = 'EXPORT_INVITATIONS_ERROR',
  EXPORT_INVITATIONS_FINISHED = 'EXPORT_INVITATIONS_FINISHED',
  EXPORT_INVITATIONS_STARTED = 'EXPORT_INVITATIONS_STARTED',
  EXPORT_PASSES_ERROR = 'EXPORT_PASSES_ERROR',
  EXPORT_PASSES_FINISHED = 'EXPORT_PASSES_FINISHED',
  EXPORT_PASSES_STARTED = 'EXPORT_PASSES_STARTED',
  EXPORT_USER_PASSES_ERROR = 'EXPORT_USER_PASSES_ERROR',
  EXPORT_USER_PASSES_FINISHED = 'EXPORT_USER_PASSES_FINISHED',
  EXPORT_USER_PASSES_STARTED = 'EXPORT_USER_PASSES_STARTED',
  HOST_UPLOAD_ERROR = 'HOST_UPLOAD_ERROR',
  HOST_UPLOAD_FINISHED = 'HOST_UPLOAD_FINISHED',
  HOST_UPLOAD_STARTED = 'HOST_UPLOAD_STARTED',
  INVITATION = 'INVITATION',
  INVITATION_BULK_UPDATE_FINISHED = 'INVITATION_BULK_UPDATE_FINISHED',
  INVITATION_UPDATED_PASS_COUNT = 'INVITATION_UPDATED_PASS_COUNT',
  INVITATION_UPLOAD_ERROR = 'INVITATION_UPLOAD_ERROR',
  INVITATION_UPLOAD_FINISHED = 'INVITATION_UPLOAD_FINISHED',
  PASS = 'PASS',
  PASS_ACCEPTED = 'PASS_ACCEPTED',
  PASS_CREATED = 'PASS_CREATED',
  PASS_CREATED_PENDING = 'PASS_CREATED_PENDING',
  PASS_CREATED_REJECTED = 'PASS_CREATED_REJECTED',
  PASS_DELETED = 'PASS_DELETED',
  PASS_EXPIRED = 'PASS_EXPIRED',
  PASS_REJECTED = 'PASS_REJECTED',
  PASS_UPDATED = 'PASS_UPDATED',
  PUBLIC_API_CREDENTIAL_ADDED = 'PUBLIC_API_CREDENTIAL_ADDED',
  PUBLIC_API_CREDENTIAL_REMOVED = 'PUBLIC_API_CREDENTIAL_REMOVED',
  SITE_DELETED = 'SITE_DELETED',
  TEAM_DELETED = 'TEAM_DELETED',
  TEAM_DETAILS = 'TEAM_DETAILS',
  TEAM_FEATURE_CHANGED = 'TEAM_FEATURE_CHANGED',
  TEAM_SITELIST = 'TEAM_SITELIST',
  USER_PERMISSIONS_CHANGED = 'USER_PERMISSIONS_CHANGED',
  USER_SOFT_DELETED = 'USER_SOFT_DELETED',
}

export type ReceiveSocketEventPayloadMap = {
  [ReceiveSocketEvent.API_KEY_CHANGED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.API_KEY_CHANGED>;
  [ReceiveSocketEvent.EXPORT_ASSETS_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_ASSETS_ERROR>;
  [ReceiveSocketEvent.EXPORT_ASSETS_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.EXPORT_ASSETS_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.EXPORT_ASSETS_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_ASSETS_STARTED>;
  [ReceiveSocketEvent.EXPORT_INVITATIONS_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_INVITATIONS_ERROR>;
  [ReceiveSocketEvent.EXPORT_INVITATIONS_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.EXPORT_INVITATIONS_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.EXPORT_INVITATIONS_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_INVITATIONS_STARTED>;
  [ReceiveSocketEvent.EXPORT_ASSET_HISTORY_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_ASSET_HISTORY_ERROR>;
  [ReceiveSocketEvent.EXPORT_ASSET_HISTORY_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.EXPORT_ASSET_HISTORY_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.EXPORT_ASSET_HISTORY_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_ASSET_HISTORY_STARTED>;
  [ReceiveSocketEvent.EXPORT_PASSES_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_PASSES_ERROR>;
  [ReceiveSocketEvent.EXPORT_PASSES_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.EXPORT_PASSES_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.EXPORT_PASSES_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_PASSES_STARTED>;
  [ReceiveSocketEvent.EXPORT_USER_PASSES_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_USER_PASSES_ERROR>;
  [ReceiveSocketEvent.EXPORT_USER_PASSES_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.EXPORT_USER_PASSES_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.EXPORT_USER_PASSES_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.EXPORT_USER_PASSES_STARTED>;
  [ReceiveSocketEvent.HOST_UPLOAD_ERROR]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.HOST_UPLOAD_ERROR,
    { email: string; failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.HOST_UPLOAD_FINISHED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.HOST_UPLOAD_FINISHED,
    { failed: any; file: { id: string; created: string } }
  >;
  [ReceiveSocketEvent.HOST_UPLOAD_STARTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.HOST_UPLOAD_STARTED>;
  [ReceiveSocketEvent.INVITATION_BULK_UPDATE_FINISHED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.INVITATION_BULK_UPDATE_FINISHED>;
  [ReceiveSocketEvent.INVITATION_UPDATED_PASS_COUNT]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.INVITATION_UPDATED_PASS_COUNT,
    Invitation
  >;
  [ReceiveSocketEvent.INVITATION_UPLOAD_ERROR]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.INVITATION_UPLOAD_ERROR>;
  [ReceiveSocketEvent.INVITATION_UPLOAD_FINISHED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.INVITATION_UPLOAD_FINISHED>;
  [ReceiveSocketEvent.INVITATION]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.INVITATION, Invitation>;
  [ReceiveSocketEvent.PASS_ACCEPTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_ACCEPTED, Pass>;
  [ReceiveSocketEvent.PASS_CREATED_PENDING]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.PASS_CREATED_PENDING,
    Pass
  >;
  [ReceiveSocketEvent.PASS_CREATED_REJECTED]: ReceiveSocketEventBasePayload<
    ReceiveSocketEvent.PASS_CREATED_REJECTED,
    Pass
  >;
  [ReceiveSocketEvent.PASS_CREATED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_CREATED, Pass>;
  [ReceiveSocketEvent.PASS_DELETED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_DELETED, Pass>;
  [ReceiveSocketEvent.PASS_EXPIRED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_EXPIRED, Pass>;
  [ReceiveSocketEvent.PASS_REJECTED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_REJECTED, Pass>;
  [ReceiveSocketEvent.PASS_UPDATED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS_UPDATED, Pass>;
  [ReceiveSocketEvent.PASS]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PASS, Pass>;
  [ReceiveSocketEvent.PUBLIC_API_CREDENTIAL_ADDED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PUBLIC_API_CREDENTIAL_ADDED>;
  [ReceiveSocketEvent.PUBLIC_API_CREDENTIAL_REMOVED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.PUBLIC_API_CREDENTIAL_REMOVED>;
  [ReceiveSocketEvent.SITE_DELETED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.SITE_DELETED, Site>;
  [ReceiveSocketEvent.TEAM_DELETED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.TEAM_DELETED, Team>;
  [ReceiveSocketEvent.TEAM_DETAILS]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.TEAM_DETAILS>;
  [ReceiveSocketEvent.TEAM_FEATURE_CHANGED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.TEAM_FEATURE_CHANGED>;
  [ReceiveSocketEvent.TEAM_SITELIST]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.TEAM_SITELIST, Site>;
  [ReceiveSocketEvent.USER_PERMISSIONS_CHANGED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.USER_PERMISSIONS_CHANGED>;
  [ReceiveSocketEvent.USER_SOFT_DELETED]: ReceiveSocketEventBasePayload<ReceiveSocketEvent.USER_SOFT_DELETED>;
};
